// Canonical date display formatting for the whole app.
// Date-only: MM-DD-YYYY
// Date+time: MM-DD-YYYY hh:mm AM/PM

const toJsDate = (value) => {
  if (!value && value !== 0) return null;

  // Firestore Timestamp instance
  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  // Plain object shaped like a Firestore Timestamp ({ seconds, nanoseconds })
  if (typeof value === "object" && value !== null && "seconds" in value) {
    return new Date(value.seconds * 1000);
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDate = (value) => {
  const d = toJsDate(value);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

export const formatDateTime = (value) => {
  const d = toJsDate(value);
  if (!d) return "";
  const datePart = formatDate(d);
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${datePart} ${hours}:${minutes} ${ampm}`;
};

const to12Hour = (hours24, minutes) => {
  const ampm = hours24 >= 12 ? "PM" : "AM";
  let hours = hours24 % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${ampm}`;
};

// Time-only: h:mm AM/PM — for fields like XCSI's Start_Time/End_Time that
// carry a full timestamp (often with a throwaway date and a "Z" suffix)
// but only the time-of-day is meaningful to show. A string value's HH:MM
// is pulled out directly with a regex rather than parsed as a real Date —
// these are wall-clock times, not real UTC instants, so routing them
// through Date/getHours() would shift the displayed hour by whatever
// timezone offset the browser happens to be in.
export const formatTime = (value) => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "string") {
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match) return value;
    return to12Hour(parseInt(match[1], 10), match[2]);
  }

  const d = toJsDate(value);
  if (!d) return "";
  return to12Hour(d.getHours(), String(d.getMinutes()).padStart(2, "0"));
};

export default formatDate;
